import { useHealthStore } from '@/stores/health';
import { useState, useEffect } from 'react';
import {
  Server,
  Database,
  Globe,
  Key,
  ExternalLink,
  Plug,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  Building2,
  Shield,
  Eye,
  EyeOff,
  RefreshCw,
  AlertCircle,
  Package,
  Download,
  BookOpen,
  Zap,
} from 'lucide-react';
import { CreativeProvidersSection } from '@/components/settings/CreativeProvidersSection';
import {
  organizationsApi,
  OrganizationSummary,
  OrganizationDetail,
  AllMCPConfigsResponse,
} from '@/services/api';

// MCP Platform info for config display
const MCP_PLATFORM_INFO: Record<string, { name: string; path: string }> = {
  claude_code: {
    name: 'Claude Code',
    path: '.claude/settings.json',
  },
  cursor: {
    name: 'Cursor',
    path: '.cursor/mcp.json',
  },
  claude_desktop: {
    name: 'Claude Desktop',
    path: '~/Library/Application Support/Claude/claude_desktop_config.json',
  },
  chatgpt: {
    name: 'ChatGPT',
    path: 'OpenAPI spec for ChatGPT Actions',
  },
};

// MCP tools summary for display
const MCP_TOOLS_SUMMARY = [
  { category: 'Personas', count: 3 },
  { category: 'Insights', count: 1 },
  { category: 'Reports', count: 1 },
  { category: 'Research', count: 1 },
  { category: 'Creative', count: 16 },
  { category: 'Generation', count: 10 },
  { category: 'Jobs', count: 3 },
];

export function SettingsPage() {
  const { ready, health } = useHealthStore();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-navy-900">Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Service configuration and environment overview. Settings are managed
          via environment variables and{' '}
          <code className="text-xs bg-slate-100 px-1 py-0.5 rounded">
            config/settings.py
          </code>
          .
        </p>
      </div>

      {/* Service info */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Server size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            Service Information
          </h2>
        </div>
        <div className="p-5 space-y-3">
          <InfoRow label="Service Name" value={health?.service ?? '—'} />
          <InfoRow label="Version" value={health?.version ?? '—'} />
          <InfoRow label="Operating Mode" value={ready?.mode ?? '—'} />
          <InfoRow
            label="Graphiti Enabled"
            value={ready?.graphiti_enabled ? 'Yes' : 'No'}
          />
        </div>
      </div>

      {/* Database / external services */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Database size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            Connected Services
          </h2>
        </div>
        <div className="p-5 space-y-3">
          <InfoRow
            label="Graph Provider"
            value={ready?.components.graph_provider ?? 'Not configured'}
          />
          <InfoRow
            label="FalkorDB URI"
            value={ready?.falkordb_uri ?? 'Not configured'}
            mono
          />
          <InfoRow
            label="Cube.dev API"
            value={ready?.cube_api_url ?? 'Not configured'}
            mono
          />
          <InfoRow
            label="Cube.dev Connected"
            value={ready?.components.cube_configured ? 'Yes' : 'No'}
          />
        </div>
      </div>

      {/* Environment variables reference */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Key size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            Environment Variables
          </h2>
        </div>
        <div className="p-5">
          <p className="text-xs text-slate-500 mb-4">
            Key environment variables used by the service. Copy{' '}
            <code className="bg-slate-100 px-1 py-0.5 rounded">
              config/env.example
            </code>{' '}
            to <code className="bg-slate-100 px-1 py-0.5 rounded">.env</code> to
            get started.
          </p>
          <div className="space-y-2">
            {envVars.map((v) => (
              <div
                key={v.name}
                className="flex items-start gap-3 py-2 border-b border-slate-100 last:border-0"
              >
                <code className="text-xs font-mono text-brand-purple bg-slate-50 px-2 py-0.5 rounded flex-shrink-0">
                  {v.name}
                </code>
                <span className="text-xs text-slate-500">{v.description}</span>
                {v.required && (
                  <span className="text-[9px] uppercase font-bold text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full flex-shrink-0">
                    required
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* MCP Connection */}
      <McpConnectionSection />

      {/* Skills Package */}
      <SkillsPackageSection />

      {/* Links */}
      <div className="flex gap-3">
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-navy-900 text-brand-teal rounded-lg text-xs font-bold hover:bg-navy-800 transition-all shadow-glow"
        >
          <ExternalLink size={14} />
          API Docs
        </a>
        <a
          href="/health"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors"
        >
          <Globe size={14} />
          Health Endpoint
        </a>
        <a
          href="/ready"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors"
        >
          <Server size={14} />
          Readiness Endpoint
        </a>
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-600">{label}</span>
      <span
        className={`text-sm font-medium text-navy-900 ${mono ? 'font-mono text-xs' : ''}`}
      >
        {value}
      </span>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }}
      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-500 hover:text-brand-purple bg-slate-50 hover:bg-slate-100 rounded transition-colors"
    >
      {copied ? <Check size={10} /> : <Copy size={10} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function McpConnectionSection() {
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationDetail | null>(
    null
  );
  const [configs, setConfigs] = useState<AllMCPConfigsResponse | null>(null);
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showToken, setShowToken] = useState(false);
  const [fullToken, setFullToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  // Fetch organizations on mount
  useEffect(() => {
    async function fetchOrgs() {
      try {
        setLoading(true);
        setError(null);
        const orgs = await organizationsApi.list();
        setOrganizations(orgs);
        // Default to neurograph-global or first org
        const defaultOrg =
          orgs.find((o) => o.org_uid === 'neurograph-global') || orgs[0];
        if (defaultOrg) {
          await selectOrg(defaultOrg.org_uid);
        }
      } catch (e) {
        setError(
          e instanceof Error ? e.message : 'Failed to load organizations'
        );
      } finally {
        setLoading(false);
      }
    }
    fetchOrgs();
  }, []);

  async function selectOrg(orgUid: string) {
    setError(null);
    setShowToken(false);
    setFullToken(null);
    setConfigs(null);

    // Load org details first (required)
    try {
      const detail = await organizationsApi.get(orgUid);
      setSelectedOrg(detail);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Failed to load organization details'
      );
      return;
    }

    // Then try to load configs (optional - may fail if token not configured)
    try {
      const allConfigs = await organizationsApi.getAllConfigs(orgUid);
      setConfigs(allConfigs);
    } catch (e) {
      // Don't set error - just leave configs as null
      // The UI will show a message about token not being configured
      console.warn(`Failed to load MCP configs for ${orgUid}:`, e);
    }
  }

  async function fetchFullToken() {
    if (!selectedOrg) return;
    try {
      setTokenLoading(true);
      const resp = await organizationsApi.getToken(selectedOrg.org_uid, false);
      setFullToken(resp.token || null);
      setShowToken(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch token');
    } finally {
      setTokenLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Plug size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            MCP Connection
          </h2>
        </div>
        <div className="p-5 flex items-center justify-center">
          <RefreshCw size={20} className="animate-spin text-slate-400" />
          <span className="ml-2 text-sm text-slate-500">
            Loading organizations...
          </span>
        </div>
      </div>
    );
  }

  if (error && !selectedOrg) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
          <Plug size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            MCP Connection
          </h2>
        </div>
        <div className="p-5">
          <div className="flex items-center gap-2 text-red-600 bg-red-50 rounded-lg px-3 py-2">
            <AlertCircle size={16} />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Plug size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">
            MCP Connection
          </h2>
        </div>
        <span className="text-[10px] uppercase font-bold text-brand-teal bg-brand-teal/10 px-2 py-0.5 rounded-full">
          Model Context Protocol
        </span>
      </div>

      <div className="p-5 space-y-5">
        {/* Organization selector */}
        <div>
          <label className="text-xs font-medium text-slate-600 mb-2 block">
            Organization
          </label>
          <div className="flex flex-wrap gap-2">
            {organizations.map((org) => (
              <button
                key={org.org_uid}
                onClick={() => selectOrg(org.org_uid)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                  selectedOrg?.org_uid === org.org_uid
                    ? 'bg-navy-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <Building2 size={12} />
                {org.display_name}
                <span
                  className={`text-[9px] px-1 py-0.5 rounded ${
                    selectedOrg?.org_uid === org.org_uid
                      ? 'bg-white/20 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {org.client_count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {selectedOrg && (
          <>
            {/* Endpoint URL */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                MCP Endpoint
              </label>
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                <code className="text-xs font-mono text-navy-900 flex-1 select-all">
                  {selectedOrg.mcp_url}
                </code>
                <CopyButton text={selectedOrg.mcp_url} />
              </div>
            </div>

            {/* Bearer Token */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block flex items-center gap-1">
                <Shield size={12} />
                Bearer Token
              </label>
              <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                {showToken && fullToken ? (
                  <>
                    <code className="text-xs font-mono text-navy-900 flex-1 select-all break-all">
                      {fullToken}
                    </code>
                    <CopyButton text={fullToken} />
                    <button
                      onClick={() => {
                        setShowToken(false);
                        setFullToken(null);
                      }}
                      className="text-slate-400 hover:text-slate-600"
                      title="Hide token"
                    >
                      <EyeOff size={14} />
                    </button>
                  </>
                ) : (
                  <>
                    <code className="text-xs font-mono text-slate-400 flex-1">
                      {selectedOrg.token_preview}
                    </code>
                    <button
                      onClick={fetchFullToken}
                      disabled={tokenLoading}
                      className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-slate-500 hover:text-brand-purple bg-white border border-slate-200 rounded transition-colors disabled:opacity-50"
                      title="Reveal full token"
                    >
                      {tokenLoading ? (
                        <RefreshCw size={10} className="animate-spin" />
                      ) : (
                        <Eye size={10} />
                      )}
                      Reveal
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Allowed Clients */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                Allowed Clients ({selectedOrg.allowed_clients.length})
              </label>
              <div className="flex flex-wrap gap-1">
                {selectedOrg.allowed_clients.slice(0, 10).map((client) => (
                  <span
                    key={client}
                    className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded"
                  >
                    {client}
                  </span>
                ))}
                {selectedOrg.allowed_clients.length > 10 && (
                  <span className="text-[10px] text-slate-400">
                    +{selectedOrg.allowed_clients.length - 10} more
                  </span>
                )}
              </div>
            </div>

            {/* Platform configs */}
            {configs ? (
              <div>
                <label className="text-xs font-medium text-slate-600 mb-2 block">
                  Connect Your AI Assistant
                </label>
                <div className="space-y-2">
                  {Object.entries(configs.configs).map(([platform, config]) => {
                    const info = MCP_PLATFORM_INFO[platform] || {
                      name: platform,
                      path: '',
                    };
                    const configStr = JSON.stringify(config, null, 2);
                    return (
                      <div
                        key={platform}
                        className="border border-slate-200 rounded-lg overflow-hidden"
                      >
                        <button
                          onClick={() =>
                            setExpandedPlatform(
                              expandedPlatform === platform ? null : platform
                            )
                          }
                          className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                        >
                          <div>
                            <span className="text-sm font-medium text-navy-900">
                              {info.name}
                            </span>
                            <span className="text-[10px] text-slate-400 ml-2">
                              {info.path}
                            </span>
                          </div>
                          {expandedPlatform === platform ? (
                            <ChevronUp size={14} className="text-slate-400" />
                          ) : (
                            <ChevronDown size={14} className="text-slate-400" />
                          )}
                        </button>
                        {expandedPlatform === platform && (
                          <div className="px-3 pb-3 border-t border-slate-100">
                            <div className="flex items-center justify-between mt-2 mb-1">
                              <span className="text-[10px] text-slate-500">
                                Add to {info.path}
                              </span>
                              <CopyButton text={configStr} />
                            </div>
                            <pre className="text-xs font-mono bg-navy-900 text-brand-teal p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                              {configStr}
                            </pre>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                <div className="flex items-start gap-2">
                  <AlertCircle size={16} className="text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">
                      MCP configs not available
                    </p>
                    <p className="text-xs text-amber-600 mt-0.5">
                      The bearer token for this organization hasn't been
                      configured in Secret Manager yet.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Enabled tools */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-2 block">
                Enabled Tool Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {selectedOrg.enabled_tools.map((tool) => (
                  <span
                    key={tool}
                    className="text-[10px] font-medium text-brand-purple bg-brand-purple/10 px-2 py-1 rounded-full capitalize"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>

            {/* Available tools summary */}
            <div>
              <label className="text-xs font-medium text-slate-600 mb-2 block">
                Available Tools (
                {MCP_TOOLS_SUMMARY.reduce((a, t) => a + t.count, 0)} total)
              </label>
              <div className="grid grid-cols-2 gap-2">
                {MCP_TOOLS_SUMMARY.map((t) => (
                  <div
                    key={t.category}
                    className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg"
                  >
                    <span className="text-xs text-slate-700">{t.category}</span>
                    <span className="text-[10px] font-bold text-brand-purple bg-brand-purple/10 px-1.5 py-0.5 rounded-full">
                      {t.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick test command */}
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
                  Test Connection
                </span>
                <CopyButton
                  text={`curl -H "Authorization: Bearer ${showToken && fullToken ? fullToken : '<TOKEN>'}" ${selectedOrg.mcp_url}`}
                />
              </div>
              <code className="text-xs font-mono text-navy-900 break-all">
                curl -H "Authorization: Bearer{' '}
                {showToken && fullToken
                  ? fullToken.slice(0, 20) + '...'
                  : '<TOKEN>'}
                " {selectedOrg.mcp_url}
              </code>
            </div>
          </>
        )}
      </div>

      {/* Provider credentials for the selected organization. Lives here rather
          than as a sibling section because the org picker above is what scopes
          it — a provider key belongs to one agency, not to the deployment. */}
      {selectedOrg && (
        <div className="px-5 pb-5">
          <CreativeProvidersSection orgUid={selectedOrg.org_uid} />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Skills Package Download Section
// =============================================================================

const SKILL_INTEGRATIONS = [
  {
    id: 'cursor',
    label: 'Cursor',
    icon: '⌨️',
    instructions: 'Copy native-skills/ contents into .claude/skills/ in your project.',
    command: 'cp -r native-skills/* .claude/skills/',
  },
  {
    id: 'claude_code',
    label: 'Claude Code',
    icon: '🖥️',
    instructions: 'Copy native-skills/ into your project\'s .claude/skills/ directory.',
    command: 'cp -r native-skills/* .claude/skills/',
  },
  {
    id: 'claude_desktop',
    label: 'Claude Desktop',
    icon: '🖥',
    instructions: 'Copy native-skills/ into ~/.claude/skills/ for global availability.',
    command: 'cp -r native-skills/* ~/.claude/skills/',
  },
  {
    id: 'claude_teams',
    label: 'Claude Teams / Claude.ai',
    icon: '👥',
    instructions: 'Upload each SKILL.md from native-skills/ in Admin Settings > Capabilities, or paste into the project system prompt.',
    command: null,
  },
];

function SkillsPackageSection() {
  const [downloading, setDownloading] = useState(false);
  const [expandedIntegration, setExpandedIntegration] = useState<string | null>(null);
  const [copiedCmd, setCopiedCmd] = useState<string | null>(null);

  async function downloadSkillsZip() {
    try {
      setDownloading(true);
      const response = await fetch('/api/v1/skills/export/zip');
      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const match = contentDisposition.match(/filename=([^;]+)/);
      const filename = match ? match[1] : `neurograph-skills-${new Date().toISOString().slice(0, 10)}.zip`;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download skills zip:', e);
    } finally {
      setDownloading(false);
    }
  }

  function copyCommand(id: string, cmd: string) {
    navigator.clipboard.writeText(cmd);
    setCopiedCmd(id);
    setTimeout(() => setCopiedCmd(null), 2000);
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={16} className="text-brand-teal" />
          <h2 className="font-semibold text-navy-900 text-sm">Skills Package</h2>
        </div>
        <span className="text-[10px] uppercase font-bold text-brand-purple bg-brand-purple/10 px-2 py-0.5 rounded-full">
          Brief-First v3
        </span>
      </div>

      <div className="p-5 space-y-5">
        <p className="text-xs text-slate-500">
          Download the complete NeuroGraph skills package for your Claude integration.
          Includes the Brief-aware Ad Pack skill with Brief Gate + Chain-Back pattern,
          plus all creative, research, and review skills.
        </p>

        {/* Download button */}
        <button
          onClick={downloadSkillsZip}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-navy-900 text-brand-teal rounded-lg text-sm font-bold hover:bg-navy-800 transition-all shadow-glow disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {downloading ? (
            <RefreshCw size={14} className="animate-spin" />
          ) : (
            <Download size={14} />
          )}
          {downloading ? 'Preparing package...' : 'Download Skills ZIP'}
        </button>

        {/* Package contents */}
        <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
          <div className="flex items-center gap-1.5 mb-2">
            <BookOpen size={12} className="text-slate-500" />
            <span className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
              Package Contents
            </span>
          </div>
          <div className="space-y-1 text-xs font-mono text-slate-600">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">📁</span>
              <span className="font-semibold">native-skills/</span>
              <span className="text-[10px] text-brand-teal font-sans font-medium">← Install these</span>
            </div>
            {[
              'ad_pack/SKILL.md ← Brief-gate + decision tree',
              'get_persona_brief/SKILL.md',
              'generate_angles/SKILL.md',
              'generate_script/SKILL.md',
              'generate_video_prompts/SKILL.md',
              'creative_review/SKILL.md',
              'landing_page_generation/ (with sub-skills + templates)',
              'seo_audit/SKILL.md ← persona-weighted, coworker-ready',
              'seo_keyword_strategy/SKILL.md',
              'seo_content_brief/SKILL.md',
              'seo_serp_tracker/SKILL.md ← weekly cron',
              'cro_audit/SKILL.md ← PDO friction map',
              'cro_experiment_plan/SKILL.md',
              'cro_funnel_analysis/SKILL.md ← weekly cron',
            ].map((f) => (
              <div key={f} className="pl-4 flex items-center gap-2 text-slate-500">
                <span>📄</span>
                <span>{f}</span>
              </div>
            ))}
            <div className="flex items-center gap-2 mt-1">
              <span className="text-slate-400">📁</span>
              <span>neurograph-skills/</span>
              <span className="text-[10px] text-slate-400 font-sans">API-compatible definitions (15 skills)</span>
            </div>
          </div>
        </div>

        {/* Integration instructions */}
        <div>
          <label className="text-xs font-medium text-slate-600 mb-2 block flex items-center gap-1.5">
            <Zap size={12} />
            Installation by Integration
          </label>
          <div className="space-y-2">
            {SKILL_INTEGRATIONS.map((integration) => (
              <div
                key={integration.id}
                className="border border-slate-200 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() =>
                    setExpandedIntegration(
                      expandedIntegration === integration.id ? null : integration.id
                    )
                  }
                  className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-base">{integration.icon}</span>
                    <span className="text-sm font-medium text-navy-900">
                      {integration.label}
                    </span>
                  </div>
                  {expandedIntegration === integration.id ? (
                    <ChevronUp size={14} className="text-slate-400" />
                  ) : (
                    <ChevronDown size={14} className="text-slate-400" />
                  )}
                </button>

                {expandedIntegration === integration.id && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-2">
                    <p className="text-xs text-slate-600">{integration.instructions}</p>
                    {integration.command && (
                      <div className="bg-navy-900 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                        <code className="text-xs font-mono text-brand-teal flex-1">
                          {integration.command}
                        </code>
                        <button
                          onClick={() => copyCommand(integration.id, integration.command!)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium text-slate-400 hover:text-brand-teal bg-white/10 rounded transition-colors flex-shrink-0"
                        >
                          {copiedCmd === integration.id ? (
                            <Check size={10} />
                          ) : (
                            <Copy size={10} />
                          )}
                          {copiedCmd === integration.id ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Brief-first note */}
        <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg px-4 py-3">
          <div className="flex items-start gap-2">
            <Zap size={14} className="text-brand-teal mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold text-navy-900 mb-0.5">
                Brief-First Workflow Included
              </p>
              <p className="text-xs text-slate-600">
                The <code className="bg-white/60 px-1 rounded">ad_pack</code> skill enforces a Brief-first
                pattern. Claude automatically detects if a Brief is missing and calls{' '}
                <code className="bg-white/60 px-1 rounded">get_creative_brief</code> first before
                generating ads — ensuring every ad pack is grounded in persona strategy, brand voice,
                and creative mandatories.
              </p>
            </div>
          </div>
        </div>

        {/* Direct API link */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>Direct API endpoint:</span>
          <code className="font-mono bg-slate-100 px-2 py-0.5 rounded text-navy-900">
            GET /api/v1/skills/export/zip
          </code>
          <CopyButton text="/api/v1/skills/export/zip" />
        </div>
      </div>
    </div>
  );
}

const envVars = [
  {
    name: 'OPENAI_API_KEY',
    description: 'OpenAI API key for LLM operations',
    required: true,
  },
  {
    name: 'EXA_API_KEY',
    description: 'Exa API key for web search in research agent',
    required: true,
  },
  {
    name: 'FALKORDB_URI',
    description: 'FalkorDB connection URI for graph database',
    required: false,
  },
  {
    name: 'CUBE_API_URL',
    description: 'Cube.dev API endpoint for insights engine',
    required: false,
  },
  {
    name: 'CUBE_API_KEY',
    description: 'Cube.dev API authentication key',
    required: false,
  },
  {
    name: 'GCP_PROJECT_ID',
    description: 'Google Cloud project for Firestore/BigQuery',
    required: false,
  },
  {
    name: 'LANGSMITH_API_KEY',
    description: 'LangSmith API key for tracing',
    required: false,
  },
  {
    name: 'LANGCHAIN_TRACING_V2',
    description: 'Enable LangChain v2 tracing',
    required: false,
  },
];
